import { colors } from '../customTheme';

const poshmarkLingo = `
~~~~light,${colors.white}
# Poshmark Lingo: Must-Know Abbreviations for Buyers and Sellers in 2025

<div style="text-align: center; font-family: Inter; margin-bottom: 6px">
Last updated August 15, 2025
</div>
<div style="text-align: center; font-family: Inter">
Written by Alice
</div>

<br /><br />

Ever felt like you're reading a foreign language when browsing Poshmark?

You're not alone.

Poshmark has developed its own unique vocabulary over the years — a mix of abbreviations, acronyms, and platform-specific terms that can confuse even the most experienced online shoppers.

From "NWT" to "CCO" to "OBO," the Poshmark community speaks in code that can make or break your buying and selling experience.

Understanding Poshmark lingo isn't just helpful — it's essential for success on the platform.

Sellers who use the right terms sell faster and for higher prices.
Buyers who understand the lingo make better purchasing decisions.
Everyone who speaks the language builds trust and credibility.

In this comprehensive guide, I'm breaking down every piece of Poshmark terminology you need to know. Whether you're a new seller, a seasoned buyer, or just curious about the platform, this is your complete dictionary to Poshmark speak.

Ready to become fluent in Poshmark lingo?

Let's decode the language together.

~~~~light,${colors.white}
#### Condition Abbreviations

These are the most important terms to understand when buying and selling on Poshmark.

##### NWT (New With Tags)
**What it means**: The item is brand new and still has the original price tags attached.

**Why it matters**: NWT items typically command the highest prices and are most desirable to buyers.

**Example**: "NWT Lululemon Align Leggings, size 6"

##### NWOT (New Without Tags)
**What it means**: The item is brand new but the original tags have been removed.

**Why it matters**: Still very valuable, but slightly less than NWT items.

**Example**: "NWOT Anthropologie dress, never worn"

##### EUC (Excellent Used Condition)
**What it means**: The item has been worn but is in excellent condition with minimal signs of wear.

**Why it matters**: High-quality used items that look almost new.

**Example**: "EUC Madewell jeans, worn 2-3 times"

##### GUC (Good Used Condition)
**What it means**: The item has been worn and shows some signs of wear but is still in good condition.

**Why it matters**: Affordable option for buyers who don't mind some wear.

**Example**: "GUC J.Crew sweater, some pilling but still wearable"

##### VGUC (Very Good Used Condition)
**What it means**: Between EUC and GUC — the item is in very good condition with minor wear.

**Why it matters**: Middle ground for pricing and condition expectations.

**Example**: "VGUC Free People top, slight fading but no stains or holes"

##### Poshmark's Official Condition Categories
- **New with tags**: Never worn, original tags attached
- **New without tags**: Never worn, tags removed
- **Excellent used condition**: Light wear, no flaws
- **Good used condition**: Normal wear, minor flaws
- **Fair used condition**: Heavy wear, visible flaws

~~~~light,${colors.white}
#### Pricing & Negotiation Terms

Understanding these terms can save you money and help you price items correctly.

##### OBO (Or Best Offer)
**What it means**: The seller is willing to accept offers below the listed price.

**Why it matters**: You can negotiate the price, but be reasonable.

**Example**: "Lululemon Define Jacket $75 OBO"

##### FIRM (Price is Firm)
**What it means**: The seller will not accept offers below the listed price.

**Why it matters**: Don't waste time making lowball offers.

**Example**: "Nike Air Max $120 FIRM"

##### OTL (Offer to Likers)
**What it means**: The seller has sent a private offer to everyone who liked the item.

**Why it matters**: You can accept the offer if you've liked the item.

**Example**: "Just sent OTL on my Lululemon leggings!"

##### CCO (Closet Clear Out)
**What it means**: A Poshmark event where sellers drop prices by at least 10% and Poshmark offers discounted shipping to likers.

**Why it matters**: Great time to buy with shipping discounts.

**Example**: "Participating in CCO today - dropping prices!"

##### Poshmark's Shipping Discounts
- **$4.99 shipping**: Standard shipping rate
- **$3.99 shipping**: Discounted shipping (often during CCO)
- **Free shipping**: Seller covers shipping cost

~~~~light,${colors.white}
#### Platform Features & Actions

These terms describe common actions and features on Poshmark.

##### Share/Sharing
**What it means**: Reposting your items to your followers' feeds to increase visibility.

**Why it matters**: Essential for getting your items seen by potential buyers.

**Example**: "Sharing my closet every 2 hours for maximum exposure"

##### Bundle
**What it means**: Multiple items purchased together from the same seller.

**Why it matters**: Often saves on shipping and can get discounts.

**Example**: "I'll bundle these 3 tops for $45 with free shipping"

##### Closet
**What it means**: A seller's collection of items for sale.

**Why it matters**: Your "store" on Poshmark.

**Example**: "Check out my closet for more great deals!"

##### Party
**What it means**: Poshmark events where users can share items in specific categories.

**Why it matters**: Great way to get exposure and find items.

**Example**: "Join the #PoshmarkParty tonight at 7 PM!"

##### Host Pick (HP)
**What it means**: Items selected by party hosts for special promotion.

**Why it matters**: Can significantly increase item visibility and sales.

**Example**: "My dress was a host pick at last night's party!"

##### Love/Like
**What it means**: Saving an item to your likes for later consideration.

**Why it matters**: Sellers can send offers to likers.

**Example**: "I loved your sweater - will you send me an offer?"

~~~~light,${colors.white}
#### Brand & Style Abbreviations

Fashion brands and styles often have their own abbreviations.

##### LLL (Lululemon)
**What it means**: Lululemon Athletica, popular athletic wear brand.

**Example**: "LLL Align leggings, size 4"

##### FP (Free People)
**What it means**: Free People, bohemian fashion brand.

**Example**: "FP maxi dress, perfect for summer"

##### AE (American Eagle)
**What it means**: American Eagle Outfitters.

**Example**: "AE jeans, high rise skinny"

##### VS (Victoria's Secret)
**What it means**: Victoria's Secret, lingerie and beauty brand.

**Example**: "VS Pink leggings, size M"

##### JC (J.Crew)
**What it means**: J.Crew, preppy fashion brand.

**Example**: "JC sweater, merino wool"

##### Anthro (Anthropologie)
**What it means**: Anthropologie, bohemian and vintage-inspired fashion.

**Example**: "Anthro dress, floral print"

##### Madewell
**What it means**: Madewell, casual and denim-focused brand.

**Example**: "Madewell jeans, perfect vintage wash"

~~~~light,${colors.white}
#### Size & Fit Terms

Understanding these terms helps with accurate sizing and fit descriptions.

##### TTS (True to Size)
**What it means**: The item fits as expected for its labeled size.

**Example**: "TTS, I'm usually a size 6 and this fits perfectly"

##### Runs Small
**What it means**: The item fits smaller than the labeled size.

**Example**: "Runs small, size up one size"

##### Runs Large
**What it means**: The item fits larger than the labeled size.

**Example**: "Runs large, size down one size"

##### Petite
**What it means**: Designed for shorter women (usually 5'4" and under).

**Example**: "Petite size 4, perfect for shorter frames"

##### Tall
**What it means**: Designed for taller women (usually 5'8" and over).

**Example**: "Tall size 6, great for longer legs"

##### Plus Size
**What it means**: Extended sizes, typically 14+ or 1X+.

**Example**: "Plus size 16, comfortable fit"

~~~~light,${colors.white}
#### Communication & Social Terms

These terms are used in comments, messages, and social interactions.

##### Poshmark Ambassador (PA)
**What it means**: Users who meet certain criteria for activity and sales on Poshmark.

**Requirements to become PA**:
- Complete 50+ sales
- Maintain a 4.5+ star rating
- Ship items within 3 days
- Respond to comments within 24 hours
- Follow community guidelines
- Have an active account for at least 90 days

**Benefits of PA status**:
- PA badge displayed on profile
- Priority customer support
- Access to exclusive events and features
- Enhanced visibility in search results
- Ability to participate in ambassador-only programs

**Why it matters**: Indicates an experienced and active user with proven reliability.

**Example**: "PA here - happy to help with any questions!"

##### Poshmark Ambassador II (PA II)
**What it means**: Higher level ambassador status with additional benefits and recognition.

**Requirements to become PA II**:
- Must first achieve PA status
- Complete 100+ sales
- Maintain a 4.8+ star rating
- Ship items within 2 days consistently
- Respond to comments within 12 hours
- Have excellent customer service record
- Participate in community activities
- Maintain active account for at least 180 days

**Additional benefits of PA II status**:
- PA II badge displayed on profile
- Highest priority customer support
- Exclusive access to beta features
- Featured placement in search results
- Invitation to exclusive events and meetups
- Potential to become a Poshmark mentor
- Access to advanced analytics and insights

**Why it matters**: Very experienced users with proven track records and exceptional service standards.

##### Welcome Bundle
**What it means**: A special offer for new buyers from a seller.

**Why it matters**: Great way to encourage first-time purchases.

**Example**: "New buyers get a welcome bundle with free shipping!"

##### Poshmark Meet (Posh Meet)
**What it means**: In-person meetups of Poshmark users.

**Why it matters**: Networking and community building.

**Example**: "Join us for the local Posh Meet this weekend!"

##### Poshmark Community Guidelines
**What it means**: Rules and standards for behavior on the platform.

**Why it matters**: Understanding these helps avoid account issues.

~~~~light,${colors.white}
#### Shipping & Transaction Terms

Important terms for the buying and selling process.

##### Poshmark Label
**What it means**: Pre-paid shipping label provided by Poshmark.

**Why it matters**: Sellers don't pay for shipping upfront.

**Example**: "I'll ship with the Poshmark label within 24 hours"

##### 3-Day Rule
**What it means**: Buyers have 3 days to accept or reject items after delivery.

**Why it matters**: Important for both buyers and sellers to understand.

##### Case (Return Case)
**What it means**: A dispute opened by a buyer for item issues when the item doesn't match the listing description or has problems.

**Why it matters**: Can result in returns, refunds, or account warnings. Understanding the process helps both buyers and sellers navigate disputes effectively.

**Common reasons for cases**:
- Item not as described (wrong size, color, condition)
- Item damaged during shipping
- Item is counterfeit or fake
- Item doesn't fit as described
- Missing items from bundle orders
- Item smells of smoke, pets, or other odors

**How the case process works**:
1. Buyer opens a case within 3 days of delivery
2. Buyer provides photos and explanation of the issue
3. Seller can respond with their side of the story
4. Poshmark Support reviews the evidence
5. Decision is made (return approved/denied, partial refund, etc.)

**Example**: "Buyer opened a case for item not as described - the listing said NWT but the item arrived without tags"

##### Poshmark Support
**What it means**: Customer service team that handles issues.

**Why it matters**: Contact them for account or transaction problems.

##### Poshmark Authentication
**What it means**: Verification process for high-value items (typically $500+) where Poshmark experts inspect items for authenticity before shipping to buyers.

**Why it matters**: Ensures authenticity of luxury items and protects both buyers and sellers from counterfeit goods.

**How it works**: 
- Seller ships item to Poshmark's authentication center
- Poshmark experts examine the item for authenticity
- If authentic, item is shipped to buyer with authentication guarantee
- If counterfeit, item is returned to seller and sale is cancelled

**Example**: "This Louis Vuitton bag will go through Poshmark Authentication before delivery"

~~~~light,${colors.white}
#### Pro Tips for Using Poshmark Lingo

##### For Sellers:
- Use condition abbreviations in your titles for better search visibility
- Include measurements and fit notes to reduce returns
- Use brand abbreviations to save space in descriptions
- Mention OBO if you're open to offers
- Use CCO strategically to boost sales

##### For Buyers:
- Search using abbreviations to find more items
- Understand condition terms to set proper expectations
- Use likes to receive OTL offers
- Bundle items to save on shipping
- Check seller's closet for more items

##### Best Practices:
- Always be honest about item condition
- Use consistent terminology in your listings
- Respond promptly to questions and offers
- Follow Poshmark community guidelines
- Build relationships with other users

~~~~light,${colors.white}
#### Final Thoughts

Poshmark lingo might seem overwhelming at first, but it's actually a powerful tool for success on the platform.

The more you understand and use these terms correctly, the better your buying and selling experience will be.

Remember: clear communication leads to better transactions, fewer returns, and happier customers.

Start with the basics — condition terms and pricing abbreviations — then gradually incorporate more advanced terminology as you become more comfortable with the platform.

Your Poshmark fluency is now complete. Use it wisely, and watch your success on the platform grow.

Happy Poshing!
`;

export default poshmarkLingo;
